import { Component, OnInit, Input, Output, EventEmitter, SimpleChanges } from '@angular/core';
import { BaseComponent } from '../base-component/base.component';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import {
  MobileLengthPerCode,
  ShippingType,
  UserStatus,
  UserType,
} from '../../enums/shared-enums.enum';
import { takeUntil } from 'rxjs/operators';
import { Country } from '../../models';
import {
  EmailValidator,
  MobileNumberLengthValidator,
  noWhitespaceValidator,
  PhoneNumberTypeValidator,
  PhoneNumberValidator,
  PhoneNumberValidatorWithException,
} from '@shared/utils/form-validators.util';
import { FunctionUtil } from '@shared/utils';
import { RolesConstants } from '@shared/constants';
import { Router } from '@angular/router';
import { forkJoin } from 'rxjs';
import { RoleAPI, SharedAPI, UserApi } from '@shared-features/services';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MatLegacyCheckboxChange } from '@angular/material/legacy-checkbox';
import { Role } from '@features/auth/models/role.model';
import { User } from '@features/users/models/user.model';

type action = 'add' | 'update';
@Component({
  selector: 'app-user-form',
  templateUrl: './user-form.component.html',
  styleUrls: ['./user-form.component.scss'],
})
export class UserFormComponent extends BaseComponent implements OnInit {
  @Input() userType: UserType;
  @Input() id: number;
  @Input() user: User;
  @Input() action: action = 'add';
  @Input() parentId: string;
  @Input() isProfile: boolean;
  @Input() shippingType: ShippingType;
  @Output() close = new EventEmitter();
  @Output() update = new EventEmitter();

  form: FormGroup;
  roles: Role[] = [];
  countries: Country[] = [];
  userStatus: Omit<UserStatus, 'All'>;
  MobileLengthPerCode = MobileLengthPerCode;
  ShippingType = ShippingType;
  UserType = UserType;
  emailValidators = [EmailValidator];
  rolesConstants = RolesConstants;

  constructor(
    public router: Router,
    private sharedAPI: SharedAPI,
    private roleAPI: RoleAPI,
    private userApi: UserApi
  ) {
    super();
  }

  ngOnInit(): void {
    this.userStatus = Object.keys(UserStatus).reduce((group, status) => {
      if (status !== 'All') {
        group[status] = UserStatus[status];
      }
      return group;
    }, {} as UserStatus);
  }

  ngOnChanges({ userType }: SimpleChanges): void {
    if (userType?.currentValue !== userType?.previousValue) {
      this.load(
        forkJoin([this.sharedAPI.getAllCountries(), this.roleAPI.getActiveRoles(this.userType)])
      )
        .pipe(takeUntil(this.destroy$))
        .subscribe(([countries, roles]) => {
          this.countries = countries;
          this.roles = roles;
          this.initForm();
        });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.complete();
  }

  onAddUser() {
    const { countryCode, mobileNumber, roles, ...formValue } = this.form.value;
    const user = Object.assign(new User(), this.user, formValue, {
      mobileNumber: countryCode + '-' + mobileNumber,
    });
    this.load(this.userApi.addUser(user, this.userType, this.id, roles), {
      successMessagePath: 'SuccessMessages.UserAddedSuccessfully',
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe((user) => {
        this.close.emit();
        this.update.emit({ user: user, shippingType: this.form.value.shippingType });
      });
  }

  onUpdateUser() {
    const { countryCode, mobileNumber, roles, ...rawFormValue } = this.form.value;
    // `EmailValidator` accepts an address padded with spaces, so trim before it is stored
    // or compared - otherwise a stray space reads as an email change.
    const formValue = { ...rawFormValue, email: rawFormValue.email?.trim() };
    const user = Object.assign(new User(), this.user, formValue, {
      mobileNumber: countryCode + '-' + mobileNumber,
    });
    const showEmailFeedback = this.isProfile && this.user.email !== formValue.email;

    const userDiff: Partial<User> = FunctionUtil.getObjectDiff(this.user, user, ['id']);

    const { country, ...rest } = userDiff;
    const body = {
      ...rest,
      countryId: country?.id,
      roleIds: roles,
    };

    this.load(this.userApi.updateUser(body), {
      successMessagePath: showEmailFeedback
        ? 'SuccessMessages.ActivationEmail'
        : 'SuccessMessages.UserUpdatedSuccessfully',
    }).subscribe((user) => {
      this.update.emit({ user: user, shippingType: this.form.value.shippingType });
    });
  }

  initForm() {
    const mobileNum = this.user?.mobileNumber;
    let roleIds = [];
    (this.user?.roles || []).forEach((role: Role) => {
      roleIds.push(role.id);
    });

    this.form = new FormGroup({
      firstName: new FormControl(this.user?.firstName || '', [
        Validators.required,
        noWhitespaceValidator,
      ]),
      lastName: new FormControl(this.user?.lastName || '', [
        Validators.required,
        noWhitespaceValidator,
      ]),
      mobileNumber: new FormControl(mobileNum?.split('-')[1] || ''),
      email: new FormControl(this.user?.email || '', [EmailValidator]),
      country: new FormControl(
        this.countries.find((c) => c.id === this.user?.country?.id) || undefined
      ),
      countryCode: new FormControl(
        mobileNum?.split('-')[0] || FunctionUtil.getSaudiArabiaPhoneKey(this.countries),
        [Validators.required]
      ),
      roles: new FormControl([...roleIds], Validators.required),
      status: new FormControl(this.user?.status),
      shippingType: new FormControl(
        this.user?.shippingType || this.shippingType || ShippingType.All
      ),
      allowToLogin: new FormControl(this.user?.allowToLogin || false, []),
    });

    this.addValidationToMobileNumber();

    if (this.user?.allowToLogin) {
      this.emailValidators.push(Validators.required);
      this.form.controls.email.setValidators(this.emailValidators);
    }
  }

  addValidationToMobileNumber(): void {
    this.form
      .get('mobileNumber')
      ?.setValidators([
        Validators.required,
        PhoneNumberValidatorWithException,
        MobileNumberLengthValidator(() => this.form.value.countryCode),
      ]);
    this.form.get('mobileNumber')?.updateValueAndValidity();
  }

  onAllowToLoginChanges(event: MatLegacyCheckboxChange) {
    const email = this.form.controls.email;
    if (event.checked) {
      this.emailValidators.push(Validators.required);
    } else {
      this.emailValidators.pop();
    }
    email.setValidators(this.emailValidators);
    email.updateValueAndValidity();
    email.markAsTouched();
  }

  isChecked(): boolean {
    return (
      this.form.controls.roles.value &&
      this.roles.length &&
      this.form.controls.roles.value.length === this.roles.length
    );
  }

  isIndeterminate(): boolean {
    return (
      this.form.controls.roles.value &&
      this.roles.length &&
      this.form.controls.roles.value.length &&
      this.form.controls.roles.value.length < this.roles.length
    );
  }

  toggleSelection(change: MatCheckboxChange): void {
    if (change.checked) {
      this.form.controls.roles.patchValue([...this.roles.map((item) => item.id)]);
    } else {
      this.form.controls.roles.patchValue([]);
    }
  }
}
